import type {
  CogoXyzAttachGithubPrPreviewInput,
  CogoXyzAttachGithubPrPreviewOutput,
  CogoXyzCogoBundle,
  CogoXyzCloudflareDeploymentRecord,
  CogoXyzCreateCogoAppInput,
  CogoXyzCreateCogoAppOutput,
  CogoXyzEvidenceArtifact,
  CogoXyzEvidenceStatus,
  CogoXyzEvidenceType,
  CogoXyzEvidencePacket,
  CogoXyzExportCogoBundleInput,
  CogoXyzExportCogoBundleOutput,
  CogoXyzGovernanceReport,
  CogoXyzArchiveLibraryItemOutput,
  CogoXyzGetLibraryItemOutput,
  CogoXyzImportFigmaFrameInput,
  CogoXyzImportFigmaFrameOutput,
  CogoXyzInspectPreviewInput,
  CogoXyzInspectPreviewOutput,
  CogoXyzLibraryItem,
  CogoXyzListCloudflareDeploymentsInput,
  CogoXyzListCloudflareDeploymentsOutput,
  CogoXyzListLibraryItemsInput,
  CogoXyzListLibraryItemsOutput,
  CogoXyzPermissionScope,
  CogoXyzPrepareDeployInput,
  CogoXyzPrepareDeployOutput,
  CogoXyzPreview,
  CogoXyzPublishGithubPrPreviewInput,
  CogoXyzPublishGithubPrPreviewOutput,
  CogoXyzPublishGalleryItemInput,
  CogoXyzPublishGalleryItemOutput,
  CogoXyzRecordCloudflareDeploymentInput,
  CogoXyzRecordCloudflareDeploymentOutput,
  CogoXyzShareCogoPreviewInput,
  CogoXyzShareCogoPreviewOutput,
  CogoXyzSourceRef,
  CogoXyzSourceType,
  CogoXyzToolRun,
  CogoXyzUpdateCogoAppInput,
  CogoXyzUpdateCogoAppOutput,
  CogoXyzWorkItem,
  CogoXyzWorkItemStatus,
} from "@cogo/types-v2";
import type { CogoXyzPreviewProofResult } from "./cogo-xyz-preview-proof";

type Clock = () => Date;
type IdFactory = (prefix: string) => string;

export const COGO_XYZ_REGISTRY_STORAGE_KEY = "cogo-studio:cogo-xyz-registry:v1";
export const COGO_XYZ_REGISTRY_SNAPSHOT_VERSION = 1;

export interface CogoXyzRegistrySnapshot {
  version: typeof COGO_XYZ_REGISTRY_SNAPSHOT_VERSION;
  workItems: CogoXyzWorkItem[];
  bundles: CogoXyzCogoBundle[];
  previews: CogoXyzPreview[];
  evidence: CogoXyzEvidenceArtifact[];
  toolRuns: CogoXyzToolRun[];
}

export interface CogoXyzRegistryStorage {
  load(): CogoXyzRegistrySnapshot | null;
  save(snapshot: CogoXyzRegistrySnapshot): void;
  clear?(): void;
}

export interface CogoXyzRegistryOptions {
  clock?: Clock;
  idFactory?: IdFactory;
  previewOrigin?: string;
  storage?: CogoXyzRegistryStorage | null;
}

interface CreateRecordsResult extends CogoXyzCreateCogoAppOutput {
  workItem: CogoXyzWorkItem;
  bundle: CogoXyzCogoBundle;
  preview: CogoXyzPreview;
  evidence: CogoXyzEvidenceArtifact;
  toolRun: CogoXyzToolRun;
}

export interface CogoXyzCreateCogoAppOptions {
  bundleRef?: string;
  previewUrl?: string;
  metadata?: Record<string, unknown>;
  toolName?: string;
}

export interface CogoXyzImportFigmaFrameOptions {
  bundleRef?: string;
  previewUrl?: string;
  frameName?: string;
  unsupportedNodes?: string[];
  matchedNodes?: number;
  nodeCount?: number;
  textNodeCount?: number;
  extractedTokens?: string[];
  matchedComponents?: string[];
  componentMatchRate?: number;
  unsupportedNodeCount?: number;
  ontologyValidation?: unknown;
  referenceParity?: unknown;
  designTemplateApplication?: unknown;
  sourceOntologyStorage?: unknown;
  sourceFrame?: unknown;
  sourceFrames?: unknown[];
  sourceFrameSelection?: unknown;
  pageCount?: number;
  actionFlowCount?: number;
  metadata?: Record<string, unknown>;
}

function defaultIdFactory(prefix: string): string {
  const random =
    globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2);
  return `${prefix}_${random.replaceAll("-", "").slice(0, 20)}`;
}

function defaultPreviewOrigin(): string {
  if (typeof window !== "undefined" && window.location?.origin)
    return window.location.origin;
  return "https://cogo.xyz";
}

function isSnapshot(value: unknown): value is CogoXyzRegistrySnapshot {
  if (!value || typeof value !== "object") return false;
  const record = value as CogoXyzRegistrySnapshot;
  return (
    record.version === COGO_XYZ_REGISTRY_SNAPSHOT_VERSION &&
    Array.isArray(record.workItems) &&
    Array.isArray(record.bundles) &&
    Array.isArray(record.previews) &&
    Array.isArray(record.evidence) &&
    Array.isArray(record.toolRuns)
  );
}

export function isCogoXyzRegistrySnapshot(
  value: unknown,
): value is CogoXyzRegistrySnapshot {
  return isSnapshot(value);
}

export class LocalStorageCogoXyzRegistryStorage implements CogoXyzRegistryStorage {
  constructor(private readonly key = COGO_XYZ_REGISTRY_STORAGE_KEY) {}

  load(): CogoXyzRegistrySnapshot | null {
    try {
      if (typeof localStorage === "undefined") return null;
      const raw = localStorage.getItem(this.key);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as unknown;
      return isSnapshot(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  save(snapshot: CogoXyzRegistrySnapshot): void {
    try {
      if (typeof localStorage === "undefined") return;
      localStorage.setItem(this.key, JSON.stringify(snapshot));
    } catch {
      // Quota or serialization failures must not break tool execution.
    }
  }

  clear(): void {
    try {
      if (typeof localStorage === "undefined") return;
      localStorage.removeItem(this.key);
    } catch {
      // ignore
    }
  }
}

function asIso(clock: Clock): string {
  return clock().toISOString();
}

function inputSnapshotRef(workItemId: string): string {
  return `cogo-xyz://work-items/${workItemId}/source-snapshot`;
}

function bundleRef(bundleId: string): string {
  return `cogo-xyz://bundles/${bundleId}`;
}

function artifactRef(kind: string, id: string): string {
  return `cogo-xyz://evidence/${kind}/${id}`;
}

function previewUrl(
  origin: string,
  workItemId: string,
  previewId: string,
): string {
  const normalizedOrigin = origin.replace(/\/$/, "");
  return `${normalizedOrigin}/ide/import-cogo-xyz-${workItemId}?surface=preview&preview=${previewId}`;
}

function galleryUrl(
  origin: string,
  workItemId: string,
  visibility: CogoXyzShareCogoPreviewInput["visibility"],
): string {
  const normalizedOrigin = origin.replace(/\/$/, "");
  return `${normalizedOrigin}/gallery/${workItemId}?visibility=${visibility}`;
}

function remixUrl(origin: string, workItemId: string): string {
  const normalizedOrigin = origin.replace(/\/$/, "");
  return `${normalizedOrigin}/studio/library?remix=${encodeURIComponent(workItemId)}`;
}

function galleryShareText(title: string, galleryUrl: string): string {
  return `${title} - made with Coogo: ${galleryUrl}`;
}

function shareUrl(
  previewUrl: string,
  visibility: CogoXyzShareCogoPreviewInput["visibility"],
): string {
  try {
    const url = new URL(previewUrl);
    url.searchParams.set("share", visibility);
    return url.toString();
  } catch {
    const separator = previewUrl.includes("?") ? "&" : "?";
    return `${previewUrl}${separator}share=${visibility}`;
  }
}

function sourceRefForCreate(
  input: CogoXyzCreateCogoAppInput,
): CogoXyzSourceRef {
  return {
    type: input.sourceType,
    label: `${input.inputType}:${input.title}`,
    metadata: {
      inputType: input.inputType,
      target: input.target,
      inputLength: input.input.length,
    },
  };
}

function sourceRefForFigma(
  input: CogoXyzImportFigmaFrameInput,
): CogoXyzSourceRef {
  return {
    type: "figma_frame",
    label: `${input.figmaFileId}:${input.figmaNodeId}`,
    url: input.figmaUrl,
    metadata: {
      figmaFileId: input.figmaFileId,
      figmaNodeId: input.figmaNodeId,
      figmaUrl: input.figmaUrl,
      audience: input.audience,
      tags: input.tags ?? [],
    },
  };
}

function githubPreviewCommentBody(
  input: CogoXyzAttachGithubPrPreviewInput,
): string {
  const uiFiles = input.uiRelatedFiles?.length
    ? input.uiRelatedFiles.map((file) => `- ${file}`).join("\n")
    : "- Not recorded";
  const changedFiles = input.changedFiles?.length
    ? input.changedFiles
        .slice(0, 20)
        .map((file) => `- ${file}`)
        .join("\n")
    : "- Not recorded";
  return [
    "## Preview with Coogo",
    "",
    `Preview: ${input.previewUrl}`,
    `Repository: ${input.repo}`,
    `PR: #${input.prNumber}`,
    input.commitSha ? `Commit: ${input.commitSha}` : null,
    input.branch || input.baseBranch
      ? `Branch: ${input.branch ?? "unknown"} -> ${input.baseBranch ?? "unknown"}`
      : null,
    "",
    "### Evidence",
    input.evidenceIds.length > 0
      ? input.evidenceIds.map((id) => `- ${id}`).join("\n")
      : "- No evidence ids supplied",
    "",
    "### UI-related files",
    uiFiles,
    "",
    "### Changed files",
    changedFiles,
  ]
    .filter((line): line is string => line !== null)
    .join("\n");
}

function githubWriteScopes(
  input: Pick<CogoXyzPublishGithubPrPreviewInput, "action">,
): CogoXyzPermissionScope[] {
  if (input.action === "comment") return ["github:write_comment"];
  if (input.action === "check") return ["github:write_check"];
  return ["github:write_comment", "github:write_check"];
}

function githubWriteActions(
  input: Pick<CogoXyzPublishGithubPrPreviewInput, "action">,
): Array<"comment" | "check"> {
  if (input.action === "comment") return ["comment"];
  if (input.action === "check") return ["check"];
  return ["comment", "check"];
}

function isExternalWritePermission(scope: CogoXyzPermissionScope): boolean {
  return [
    "github:write_comment",
    "github:write_check",
    "github:create_pr",
    "figma:write_frame",
    "figma:create_file",
    "cogo:publish_gallery",
    "deploy:create",
  ].includes(scope);
}

function evidenceStatusForCloudflare(status: string): CogoXyzEvidenceStatus {
  const normalized = status.toLowerCase();
  if (normalized === "success" || normalized === "active") return "deployed";
  if (
    normalized === "failure" ||
    normalized === "failed" ||
    normalized === "canceled"
  )
    return "failed";
  return "created";
}

function makeToolRun(params: {
  id: string;
  workspaceId: string;
  toolName: string;
  input: Record<string, unknown>;
  output?: Record<string, unknown>;
  permissionScope: CogoXyzPermissionScope[];
  createdBy?: string;
  createdAt: string;
  durationMs: number;
  status?: CogoXyzToolRun["status"];
  error?: string;
}): CogoXyzToolRun {
  return {
    id: params.id,
    workspaceId: params.workspaceId,
    toolName: params.toolName,
    input: params.input,
    ...(params.output === undefined ? {} : { output: params.output }),
    status: params.status ?? "succeeded",
    permissionScope: params.permissionScope,
    ...(params.createdBy === undefined ? {} : { createdBy: params.createdBy }),
    createdAt: params.createdAt,
    durationMs: params.durationMs,
    ...(params.error === undefined ? {} : { error: params.error }),
  };
}

export class CogoXyzRegistry {
  private readonly clock: Clock;
  private readonly idFactory: IdFactory;
  private readonly previewOrigin: string;
  private readonly storage: CogoXyzRegistryStorage | null;
  private readonly workItems = new Map<string, CogoXyzWorkItem>();
  private readonly bundles = new Map<string, CogoXyzCogoBundle>();
  private readonly previews = new Map<string, CogoXyzPreview>();
  private readonly evidence = new Map<string, CogoXyzEvidenceArtifact>();
  private readonly toolRuns = new Map<string, CogoXyzToolRun>();
  private readonly previewByUrl = new Map<string, string>();
  private readonly listeners = new Set<() => void>();
  private revision = 0;

  constructor(options: CogoXyzRegistryOptions = {}) {
    this.clock = options.clock ?? (() => new Date());
    this.idFactory = options.idFactory ?? defaultIdFactory;
    this.previewOrigin = options.previewOrigin ?? defaultPreviewOrigin();
    this.storage =
      options.storage === undefined
        ? new LocalStorageCogoXyzRegistryStorage()
        : options.storage;
    this.hydrate();
  }

  createCogoApp(
    input: CogoXyzCreateCogoAppInput,
    options: CogoXyzCreateCogoAppOptions = {},
  ): CreateRecordsResult {
    const startedAt = this.clock().getTime();
    const createdAt = asIso(this.clock);
    const workItem = this.createWorkItem({
      workspaceId: input.workspaceId,
      title: input.title,
      sourceType: input.sourceType,
      sourceRef: sourceRefForCreate(input),
      createdBy: input.createdBy,
      status: "preview_generated",
      createdAt,
    });
    const bundle = this.createBundle(workItem.id, createdAt, options);
    const preview = this.createPreview(
      workItem.id,
      bundle.id,
      createdAt,
      options.previewUrl,
    );
    const evidence = this.recordEvidence({
      workItemId: workItem.id,
      type: "preview_link",
      artifactRef: preview.previewUrl,
      summary: `Preview generated for ${input.sourceType} ${input.inputType} input.`,
      status: "preview_generated",
      createdAt,
      metadata: {
        target: input.target,
        bundleId: bundle.id,
        previewId: preview.id,
        ...options.metadata,
      },
    });
    const output: CogoXyzCreateCogoAppOutput = {
      workItemId: workItem.id,
      bundleId: bundle.id,
      previewUrl: preview.previewUrl,
      evidenceId: evidence.id,
      status: "preview_generated",
    };
    const toolRun = this.recordToolRun(
      makeToolRun({
        id: this.idFactory("toolrun"),
        workspaceId: input.workspaceId,
        toolName: options.toolName ?? "create_cogo_app",
        input: input as unknown as Record<string, unknown>,
        output: output as unknown as Record<string, unknown>,
        permissionScope: ["cogo:create_bundle", "cogo:create_preview"],
        createdAt,
        durationMs: this.clock().getTime() - startedAt,
      }),
    );
    return { ...output, workItem, bundle, preview, evidence, toolRun };
  }

  importFigmaFrame(
    input: CogoXyzImportFigmaFrameInput,
    options: CogoXyzImportFigmaFrameOptions = {},
  ): CogoXyzImportFigmaFrameOutput {
    const createdAt = asIso(this.clock);
    const workItem = this.createWorkItem({
      workspaceId: input.workspaceId,
      title: options.frameName ?? `Figma frame ${input.figmaNodeId}`,
      sourceType: "figma_frame",
      sourceRef: sourceRefForFigma(input),
      createdBy: input.createdBy,
      status: input.createPreview ? "preview_generated" : "imported",
      createdAt,
    });
    const bundle = this.createBundle(workItem.id, createdAt, {
      bundleRef: options.bundleRef,
    });
    const preview = this.createPreview(
      workItem.id,
      bundle.id,
      createdAt,
      options.previewUrl,
    );
    const unsupportedNodes = options.unsupportedNodes ?? [];
    const evidence = this.recordEvidence({
      workItemId: workItem.id,
      type: "figma_mapping",
      artifactRef: artifactRef("figma-mapping", workItem.id),
      summary: `Imported Figma frame ${input.figmaNodeId} from file ${input.figmaFileId}.`,
      status: input.createPreview ? "preview_generated" : "created",
      createdAt,
      metadata: {
        figmaFileId: input.figmaFileId,
        figmaNodeId: input.figmaNodeId,
        figmaUrl: input.figmaUrl,
        frameName: options.frameName,
        matchedNodes: options.matchedNodes,
        nodeCount: options.nodeCount,
        textNodeCount: options.textNodeCount,
        extractedTokens: options.extractedTokens,
        matchedComponents: options.matchedComponents ?? [],
        componentMatchRate: options.componentMatchRate,
        unsupportedNodeCount:
          options.unsupportedNodeCount ?? unsupportedNodes.length,
        ontologyValidation: options.ontologyValidation,
        referenceParity: options.referenceParity,
        designTemplateApplication: options.designTemplateApplication,
        sourceOntologyStorage: options.sourceOntologyStorage,
        sourceFrame: options.sourceFrame,
        sourceFrameSelection: options.sourceFrameSelection,
        audience: input.audience,
        tags: input.tags ?? [],
        unsupportedNodes,
        ...options.metadata,
      },
    });
    const toolRunInput = {
      workspaceId: input.workspaceId,
      figmaFileId: input.figmaFileId,
      figmaNodeId: input.figmaNodeId,
      figmaUrl: input.figmaUrl,
      createPreview: input.createPreview,
      createdBy: input.createdBy,
      audience: input.audience,
      tags: input.tags,
    };
    this.recordToolRun(
      makeToolRun({
        id: this.idFactory("toolrun"),
        workspaceId: input.workspaceId,
        toolName: "import_figma_frame",
        input: toolRunInput as unknown as Record<string, unknown>,
        output: {
          workItemId: workItem.id,
          bundleId: bundle.id,
          previewUrl: preview.previewUrl,
          mappingEvidenceId: evidence.id,
          unsupportedNodes,
          matchedNodes: options.matchedNodes,
          nodeCount: options.nodeCount,
          textNodeCount: options.textNodeCount,
          extractedTokens: options.extractedTokens,
          frameName: options.frameName,
          figmaUrl: input.figmaUrl,
          matchedComponents: options.matchedComponents ?? [],
          componentMatchRate: options.componentMatchRate,
          unsupportedNodeCount:
            options.unsupportedNodeCount ?? unsupportedNodes.length,
          ontologyValidation: options.ontologyValidation,
          referenceParity: options.referenceParity,
          designTemplateApplication: options.designTemplateApplication,
          sourceOntologyStorage: options.sourceOntologyStorage,
          sourceFrame: options.sourceFrame,
          sourceFrames: options.sourceFrames,
          sourceFrameSelection: options.sourceFrameSelection,
          pageCount: options.pageCount,
          actionFlowCount: options.actionFlowCount,
        },
        permissionScope: [
          "figma:read_file",
          "figma:read_frame",
          "cogo:create_bundle",
          "cogo:create_preview",
        ],
        createdAt,
        durationMs: 0,
      }),
    );
    return {
      workItemId: workItem.id,
      bundleId: bundle.id,
      previewUrl: preview.previewUrl,
      mappingEvidenceId: evidence.id,
      unsupportedNodes,
      matchedNodes: options.matchedNodes,
      nodeCount: options.nodeCount,
      textNodeCount: options.textNodeCount,
      extractedTokens: options.extractedTokens,
      frameName: options.frameName,
      figmaUrl: input.figmaUrl,
      matchedComponents: options.matchedComponents ?? [],
      componentMatchRate: options.componentMatchRate,
      unsupportedNodeCount:
        options.unsupportedNodeCount ?? unsupportedNodes.length,
      ontologyValidation: options.ontologyValidation,
      referenceParity: options.referenceParity,
      designTemplateApplication: options.designTemplateApplication,
      sourceOntologyStorage: options.sourceOntologyStorage,
      sourceFrame: options.sourceFrame,
      sourceFrames: options.sourceFrames,
      sourceFrameSelection: options.sourceFrameSelection,
      pageCount: options.pageCount,
      actionFlowCount: options.actionFlowCount,
    };
  }

  inspectPreview(
    input: CogoXyzInspectPreviewInput,
    proofResult?: CogoXyzPreviewProofResult,
  ): CogoXyzInspectPreviewOutput {
    const startedAt = this.clock().getTime();
    const previewId = this.previewByUrl.get(input.previewUrl);
    const preview = previewId ? this.previews.get(previewId) : undefined;
    const createdAt = asIso(this.clock);
    const workItemId =
      preview?.workItemId ?? this.idFactory("external-workitem");
    const workItem = this.workItems.get(workItemId);
    const resolvedProof = proofResult ?? {
      status: "partial" as const,
      summary: "Inspection evidence was recorded; browser proof is pending.",
      artifacts: [
        {
          type: "browser_check" as CogoXyzEvidenceType,
          artifactRef: artifactRef(
            "browser-check",
            this.idFactory("inspection"),
          ),
          summary: `Preview inspection requested for checks: ${input.checks.join(", ") || "none"}. Browser execution pending.`,
          status: "created" as CogoXyzEvidenceStatus,
          metadata: {
            previewUrl: input.previewUrl,
            checks: input.checks,
            proofLevel: "partial",
          },
        },
      ],
      metadata: {},
    };
    const evidence = resolvedProof.artifacts.map((artifact) =>
      this.recordEvidence({
        workItemId,
        type: artifact.type,
        artifactRef: artifact.artifactRef,
        summary: artifact.summary,
        status: artifact.status,
        createdAt,
        metadata: {
          previewUrl: input.previewUrl,
          checks: input.checks,
          proofStatus: resolvedProof.status,
          ...resolvedProof.metadata,
          ...artifact.metadata,
        },
      }),
    );
    if (preview) {
      if (resolvedProof.status === "passed")
        this.updatePreviewStatus(preview.id, "browser_checked");
      this.updateWorkItemStatus(preview.workItemId, "inspected", createdAt);
    }
    const output: CogoXyzInspectPreviewOutput = {
      evidenceIds: evidence.map((item) => item.id),
      status: resolvedProof.status,
      summary: resolvedProof.summary,
    };
    this.recordToolRun(
      makeToolRun({
        id: this.idFactory("toolrun"),
        workspaceId: workItem?.workspaceId ?? "external",
        toolName: "inspect_cogo_preview",
        input: input as unknown as Record<string, unknown>,
        output: output as unknown as Record<string, unknown>,
        permissionScope: ["cogo:read_preview"],
        createdAt,
        durationMs: this.clock().getTime() - startedAt,
      }),
    );
    return output;
  }

  updateCogoApp(input: CogoXyzUpdateCogoAppInput): CogoXyzUpdateCogoAppOutput {
    const startedAt = this.clock().getTime();
    const createdAt = asIso(this.clock);
    const workItem = this.workItems.get(input.workItemId);
    if (!workItem)
      throw new Error(`Unknown Library Hub WorkItem: ${input.workItemId}`);

    const bundle = this.createBundle(input.workItemId, createdAt);
    const preview = input.createPreview
      ? this.createPreview(input.workItemId, bundle.id, createdAt)
      : undefined;
    const diffSummary = `Update instruction recorded with ${input.patchMode} patch mode.`;
    const evidence = this.recordEvidence({
      workItemId: input.workItemId,
      type: "cogo_json_diff",
      artifactRef: artifactRef("cogo-json-diff", this.idFactory("diff")),
      summary: diffSummary,
      status: input.createPreview ? "preview_generated" : "created",
      createdAt,
      metadata: {
        instruction: input.instruction,
        patchMode: input.patchMode,
        createPreview: input.createPreview,
        bundleId: bundle.id,
        previewId: preview?.id,
        previewUrl: preview?.previewUrl,
      },
    });
    this.updateWorkItemStatus(
      input.workItemId,
      input.createPreview ? "preview_generated" : "converted",
      createdAt,
    );

    const output: CogoXyzUpdateCogoAppOutput = {
      bundleId: bundle.id,
      previewUrl: preview?.previewUrl ?? "",
      diffSummary,
      status: "updated",
    };
    this.recordToolRun(
      makeToolRun({
        id: this.idFactory("toolrun"),
        workspaceId: workItem.workspaceId,
        toolName: "update_cogo_app",
        input: input as unknown as Record<string, unknown>,
        output: {
          ...output,
          evidenceId: evidence.id,
        },
        permissionScope: [
          "cogo:update_bundle",
          ...(input.createPreview ? ["cogo:create_preview" as const] : []),
        ],
        createdAt,
        durationMs: this.clock().getTime() - startedAt,
      }),
    );
    return output;
  }

  attachGithubPrPreview(
    input: CogoXyzAttachGithubPrPreviewInput,
  ): CogoXyzAttachGithubPrPreviewOutput {
    const startedAt = this.clock().getTime();
    if (input.commentMode === "post") {
      throw new Error(
        'Posting GitHub comments requires an approved write integration; use commentMode="draft" for Phase 1.',
      );
    }
    const createdAt = asIso(this.clock);
    const workItemId =
      this.resolveWorkItemIdForPreview(input.previewUrl) ??
      this.idFactory("external-workitem");
    const commentBody = githubPreviewCommentBody(input);
    const evidence = this.recordEvidence({
      workItemId,
      type: "github_comment",
      artifactRef: `draft://github/${input.repo}/pull/${input.prNumber}/cogo-preview`,
      summary: `Draft GitHub PR preview comment for ${input.repo}#${input.prNumber}.`,
      status: "created",
      createdAt,
      metadata: {
        repo: input.repo,
        prNumber: input.prNumber,
        previewUrl: input.previewUrl,
        evidenceIds: input.evidenceIds,
        commentMode: input.commentMode,
        commentBody,
        checkName: "Coogo Preview",
        checkStatus: "pending_write",
        commitSha: input.commitSha,
        branch: input.branch,
        baseBranch: input.baseBranch,
        changedFiles: input.changedFiles,
        uiRelatedFiles: input.uiRelatedFiles,
      },
    });
    this.updateWorkItemStatus(workItemId, "attached", createdAt);
    const output: CogoXyzAttachGithubPrPreviewOutput = {
      workItemId,
      evidenceId: evidence.id,
      githubCommentUrl: evidence.artifactRef,
      commentBody,
      checkName: "Coogo Preview",
      checkStatus: "pending_write",
      status: "drafted",
    };
    this.recordToolRun(
      makeToolRun({
        id: this.idFactory("toolrun"),
        workspaceId: input.workspaceId,
        toolName: "attach_github_pr_preview",
        input: input as unknown as Record<string, unknown>,
        output: output as unknown as Record<string, unknown>,
        permissionScope: ["github:read_pr", "cogo:read_preview"],
        createdBy: input.createdBy,
        createdAt,
        durationMs: this.clock().getTime() - startedAt,
      }),
    );
    return output;
  }

  publishGithubPrPreview(
    input: CogoXyzPublishGithubPrPreviewInput,
  ): CogoXyzPublishGithubPrPreviewOutput {
    const startedAt = this.clock().getTime();
    const createdAt = asIso(this.clock);
    const workItem = this.workItems.get(input.workItemId);
    if (!workItem)
      throw new Error(`Unknown Library Hub WorkItem: ${input.workItemId}`);
    const actions = githubWriteActions(input);
    const writeScopes = githubWriteScopes(input);
    const isExecute = input.mode === "execute";
    const approvalRequired = isExecute && input.approved !== true;
    const posted = isExecute && input.approved === true;
    const status: CogoXyzPublishGithubPrPreviewOutput["status"] =
      input.mode === "dry_run"
        ? "dry_run"
        : approvalRequired
          ? "approval_required"
          : posted
            ? "posted"
            : "approved";
    const checkStatus: CogoXyzPublishGithubPrPreviewOutput["checkStatus"] =
      input.mode === "dry_run"
        ? "pending_write"
        : posted
          ? "completed"
          : approvalRequired
            ? "pending_write"
            : "approved";
    const evidenceIds = actions.map((action) => {
      const isComment = action === "comment";
      const artifactRef = isComment
        ? (input.githubCommentUrl ??
          `${status}://github/${input.repo}/pull/${input.prNumber}/cogo-preview-comment`)
        : (input.githubCheckUrl ??
          `${status}://github/${input.repo}/pull/${input.prNumber}/cogo-preview-check`);
      return this.recordEvidence({
        workItemId: input.workItemId,
        type: isComment ? "github_comment" : "github_check",
        artifactRef,
        summary: `${status === "posted" ? "Posted" : status === "approved" ? "Approved" : "Prepared"} GitHub PR ${isComment ? "comment" : "check"} for ${input.repo}#${input.prNumber}.`,
        status: posted ? "attached_to_source" : "created",
        createdAt,
        metadata: {
          repo: input.repo,
          prNumber: input.prNumber,
          previewUrl: input.previewUrl,
          commentBody: input.commentBody,
          action,
          writeMode: input.mode,
          approved: input.approved === true,
          approvalRequired,
          externalWriteExecuted: posted,
          checkName: "Coogo Preview",
          checkStatus,
          commitSha: input.commitSha,
          branch: input.branch,
          githubCommentUrl: input.githubCommentUrl,
          githubCheckUrl: input.githubCheckUrl,
        },
      }).id;
    });
    if (!approvalRequired && input.mode !== "dry_run")
      this.updateWorkItemStatus(input.workItemId, "attached", createdAt);
    const output: CogoXyzPublishGithubPrPreviewOutput = {
      workItemId: input.workItemId,
      evidenceIds,
      status,
      approvalRequired,
      externalWritePermissions:
        approvalRequired || input.mode === "dry_run" ? [] : writeScopes,
      githubCommentUrl: input.githubCommentUrl,
      githubCheckUrl: input.githubCheckUrl,
      checkName: "Coogo Preview",
      checkStatus,
    };
    this.recordToolRun(
      makeToolRun({
        id: this.idFactory("toolrun"),
        workspaceId: workItem.workspaceId,
        toolName: "publish_github_pr_preview",
        input: input as unknown as Record<string, unknown>,
        output: output as unknown as Record<string, unknown>,
        permissionScope: [
          "github:read_pr",
          "cogo:read_preview",
          ...output.externalWritePermissions,
        ],
        createdBy: input.createdBy,
        createdAt,
        durationMs: this.clock().getTime() - startedAt,
      }),
    );
    return output;
  }

  shareCogoPreview(
    input: CogoXyzShareCogoPreviewInput,
  ): CogoXyzShareCogoPreviewOutput {
    const startedAt = this.clock().getTime();
    const createdAt = asIso(this.clock);
    const previewId = this.previewByUrl.get(input.previewUrl);
    const preview = previewId ? this.previews.get(previewId) : undefined;
    if (!preview)
      throw new Error(`Unknown Library Hub preview: ${input.previewUrl}`);
    const workItem = this.workItems.get(preview.workItemId);
    const sharedUrl = shareUrl(input.previewUrl, input.visibility);
    const evidence = this.recordEvidence({
      workItemId: preview.workItemId,
      type: "preview_link",
      artifactRef: sharedUrl,
      summary: `Shared ${input.visibility} Coogo preview link.`,
      status: "attached_to_source",
      createdAt,
      metadata: {
        previewUrl: input.previewUrl,
        shareUrl: sharedUrl,
        visibility: input.visibility,
        previewId: preview.id,
        bundleId: preview.bundleId,
      },
    });
    this.updateWorkItemStatus(preview.workItemId, "attached", createdAt);
    const output: CogoXyzShareCogoPreviewOutput = {
      shareUrl: sharedUrl,
      evidenceId: evidence.id,
      status: "shared",
    };
    this.recordToolRun(
      makeToolRun({
        id: this.idFactory("toolrun"),
        workspaceId: workItem?.workspaceId ?? "external",
        toolName: "share_cogo_preview",
        input: input as unknown as Record<string, unknown>,
        output: output as unknown as Record<string, unknown>,
        permissionScope: ["cogo:read_preview"],
        createdAt,
        durationMs: this.clock().getTime() - startedAt,
      }),
    );
    return output;
  }

  exportCogoBundle(
    input: CogoXyzExportCogoBundleInput,
  ): CogoXyzExportCogoBundleOutput {
    const startedAt = this.clock().getTime();
    const bundle = this.bundles.get(input.bundleId);
    if (!bundle) {
      throw new Error(`Unknown Coogo bundle: ${input.bundleId}`);
    }
    const workItem = this.workItems.get(bundle.workItemId);
    const createdAt = asIso(this.clock);
    const output: CogoXyzExportCogoBundleOutput = {
      artifactRef: `cogo-xyz://exports/${input.format}/${input.bundleId}`,
      format: input.format,
      status: "exported",
    };
    this.recordToolRun(
      makeToolRun({
        id: this.idFactory("toolrun"),
        workspaceId: workItem?.workspaceId ?? "external",
        toolName: "export_cogo_bundle",
        input: input as unknown as Record<string, unknown>,
        output: output as unknown as Record<string, unknown>,
        permissionScope: ["cogo:read_bundle"],
        createdAt,
        durationMs: this.clock().getTime() - startedAt,
      }),
    );
    return output;
  }

  prepareCogoDeploy(
    input: CogoXyzPrepareDeployInput,
  ): CogoXyzPrepareDeployOutput {
    const startedAt = this.clock().getTime();
    const createdAt = asIso(this.clock);
    const bundle = this.bundles.get(input.bundleId);
    if (!bundle) throw new Error(`Unknown Coogo bundle: ${input.bundleId}`);
    const workItem = this.workItems.get(bundle.workItemId);
    const preview = Array.from(this.previews.values())
      .reverse()
      .find((item) => item.bundleId === bundle.id);
    const approvalRequired =
      input.target === "production" && input.approved !== true;
    const hasExternalDeployRecord = Boolean(
      input.deploymentId || input.productionUrl,
    );
    const deployArtifactRef = `cogo-xyz://exports/deploy_package/${bundle.id}`;
    const deploymentTimestamp = createdAt;
    const evidence = this.recordEvidence({
      workItemId: bundle.workItemId,
      type: "deploy_record",
      artifactRef: deployArtifactRef,
      summary: approvalRequired
        ? `Production deployment for ${input.provider} requires explicit approval.`
        : `Prepared ${input.provider} ${input.target} deployment package.`,
      status:
        !approvalRequired && hasExternalDeployRecord ? "deployed" : "created",
      createdAt,
      metadata: {
        provider: input.provider,
        target: input.target,
        previewUrl: preview?.previewUrl,
        productionUrl: input.productionUrl,
        sourceSha: input.sourceSha,
        deploymentId: input.deploymentId,
        deploymentTimestamp,
        approvalRequired,
      },
    });
    if (!approvalRequired && hasExternalDeployRecord)
      this.updateWorkItemStatus(bundle.workItemId, "deployed", createdAt);
    const output: CogoXyzPrepareDeployOutput = {
      deployArtifactRef,
      deploymentEvidenceId: evidence.id,
      status: approvalRequired ? "approval_required" : "prepared",
      provider: input.provider,
      target: input.target,
      previewUrl: preview?.previewUrl,
      productionUrl: input.productionUrl,
      sourceSha: input.sourceSha,
      deploymentId: input.deploymentId,
      deploymentTimestamp,
    };
    this.recordToolRun(
      makeToolRun({
        id: this.idFactory("toolrun"),
        workspaceId: workItem?.workspaceId ?? "external",
        toolName: "prepare_cogo_deploy",
        input: input as unknown as Record<string, unknown>,
        output: output as unknown as Record<string, unknown>,
        permissionScope: approvalRequired
          ? ["cogo:read_bundle"]
          : ["cogo:read_bundle", "deploy:create"],
        createdAt,
        durationMs: this.clock().getTime() - startedAt,
      }),
    );
    return output;
  }

  recordCloudflareDeployment(
    input: CogoXyzRecordCloudflareDeploymentInput,
  ): CogoXyzRecordCloudflareDeploymentOutput {
    const startedAt = this.clock().getTime();
    const createdAt = asIso(this.clock);
    const bundle = input.bundleId
      ? this.bundles.get(input.bundleId)
      : input.workItemId
        ? Array.from(this.bundles.values())
            .reverse()
            .find((item) => item.workItemId === input.workItemId)
        : undefined;
    const workItem = input.workItemId
      ? this.workItems.get(input.workItemId)
      : bundle
        ? this.workItems.get(bundle.workItemId)
        : undefined;
    if (!workItem)
      throw new Error(
        `Unknown Library Hub WorkItem for Cloudflare deployment.`,
      );
    const deployedAt = input.deployedAt ?? createdAt;
    const evidenceStatus = evidenceStatusForCloudflare(input.status);
    const artifactRef = `cloudflare://${input.surface}/${input.projectName}/deployments/${input.deploymentId}`;
    const recordId = this.idFactory("cfdeploy");
    const recordWithoutEvidence = {
      id: recordId,
      workItemId: workItem.id,
      bundleId: bundle?.id,
      surface: input.surface,
      accountId: input.accountId,
      projectName: input.projectName,
      environment: input.environment,
      deploymentId: input.deploymentId,
      versionId: input.versionId,
      deploymentUrl: input.deploymentUrl,
      previewUrl: input.previewUrl,
      productionUrl: input.productionUrl,
      status: input.status,
      gitSource: input.gitSource,
      deployedAt,
    };
    const evidence = this.recordEvidence({
      workItemId: workItem.id,
      type: "deploy_record",
      artifactRef,
      summary: `Recorded Cloudflare ${input.surface} ${input.environment} deployment for ${input.projectName}.`,
      status: evidenceStatus,
      createdAt,
      metadata: {
        provider:
          input.surface === "pages" ? "cloudflare_pages" : "cloudflare_workers",
        target: input.environment,
        previewUrl: input.previewUrl,
        productionUrl: input.productionUrl,
        sourceSha: input.gitSource.commitSha,
        deploymentId: input.deploymentId,
        versionId: input.versionId,
        deploymentTimestamp: deployedAt,
        approvalRequired: false,
        gitSource: input.gitSource,
      },
    });
    const record: CogoXyzCloudflareDeploymentRecord = {
      ...recordWithoutEvidence,
      evidenceId: evidence.id,
    };
    this.evidence.set(evidence.id, {
      ...evidence,
      metadata: {
        ...evidence.metadata,
        cloudflareDeployment: record,
      },
    });
    if (evidenceStatus === "deployed" && input.environment === "production") {
      this.updateWorkItemStatus(workItem.id, "deployed", createdAt);
    } else if (evidenceStatus === "failed") {
      this.updateWorkItemStatus(workItem.id, "failed", createdAt);
    } else {
      this.persist();
    }
    const output: CogoXyzRecordCloudflareDeploymentOutput = {
      record,
      evidenceId: evidence.id,
      proofLevel:
        this.getGovernanceReport(workItem.id)?.proofLevel ??
        (evidenceStatus === "deployed" ? "deployed" : "converted"),
      status: evidenceStatus === "deployed" ? "deployed" : "recorded",
    };
    this.recordToolRun(
      makeToolRun({
        id: this.idFactory("toolrun"),
        workspaceId: workItem.workspaceId,
        toolName: "record_cloudflare_deployment",
        input: input as unknown as Record<string, unknown>,
        output: output as unknown as Record<string, unknown>,
        permissionScope: ["cogo:read_bundle", "deploy:create"],
        createdBy: input.createdBy,
        createdAt,
        durationMs: this.clock().getTime() - startedAt,
      }),
    );
    return output;
  }

  listCloudflareDeployments(
    input: CogoXyzListCloudflareDeploymentsInput = {},
  ): CogoXyzListCloudflareDeploymentsOutput {
    const environment =
      input.environment && input.environment !== "all"
        ? input.environment
        : null;
    const limit =
      typeof input.limit === "number" &&
      Number.isFinite(input.limit) &&
      input.limit > 0
        ? Math.floor(input.limit)
        : null;
    const records = Array.from(this.evidence.values())
      .flatMap((item) => {
        const record = item.metadata?.cloudflareDeployment;
        return record && typeof record === "object"
          ? [record as CogoXyzCloudflareDeploymentRecord]
          : [];
      })
      .filter(
        (record) => !input.workItemId || record.workItemId === input.workItemId,
      )
      .filter(
        (record) =>
          !input.projectName || record.projectName === input.projectName,
      )
      .filter((record) => !environment || record.environment === environment)
      .filter(
        (record) =>
          !input.commitSha || record.gitSource.commitSha === input.commitSha,
      )
      .sort((a, b) => b.deployedAt.localeCompare(a.deployedAt));
    return {
      records: limit ? records.slice(0, limit) : records,
      total: records.length,
    };
  }

  publishGalleryItem(
    input: CogoXyzPublishGalleryItemInput,
  ): CogoXyzPublishGalleryItemOutput {
    const startedAt = this.clock().getTime();
    const createdAt = asIso(this.clock);
    const workItem = this.workItems.get(input.workItemId);
    if (!workItem)
      throw new Error(`Unknown Library Hub WorkItem: ${input.workItemId}`);
    const preview = Array.from(this.previews.values())
      .reverse()
      .find((item) => item.workItemId === input.workItemId);
    const bundle = Array.from(this.bundles.values())
      .reverse()
      .find((item) => item.workItemId === input.workItemId);
    const title = input.title ?? workItem.title;
    const publishedUrl = galleryUrl(
      this.previewOrigin,
      input.workItemId,
      input.visibility,
    );
    const remix = remixUrl(this.previewOrigin, input.workItemId);
    const shareText = galleryShareText(title, publishedUrl);
    const madeWithCogoBadge = "made with Coogo";
    const evidence = this.recordEvidence({
      workItemId: input.workItemId,
      type: "gallery_publish",
      artifactRef: publishedUrl,
      summary: `Published ${input.visibility} Coogo gallery item.`,
      status: "attached_to_source",
      createdAt,
      metadata: {
        title,
        visibility: input.visibility,
        galleryUrl: publishedUrl,
        remixUrl: remix,
        shareText,
        madeWithCogoBadge,
        publicAttribution: "made_with_cogo",
        audience: input.audience,
        tags: input.tags ?? [],
        remixSourceTemplateId: input.remixSourceTemplateId,
        previewUrl: preview?.previewUrl,
        bundleId: bundle?.id,
      },
    });
    this.updateWorkItemStatus(input.workItemId, "attached", createdAt);
    const output: CogoXyzPublishGalleryItemOutput = {
      galleryUrl: publishedUrl,
      remixUrl: remix,
      shareText,
      madeWithCogoBadge,
      publicAttribution: "made_with_cogo",
      evidenceId: evidence.id,
      status: "published",
    };
    this.recordToolRun(
      makeToolRun({
        id: this.idFactory("toolrun"),
        workspaceId: workItem.workspaceId,
        toolName: "publish_cogo_gallery_item",
        input: input as unknown as Record<string, unknown>,
        output: output as unknown as Record<string, unknown>,
        permissionScope: ["cogo:read_preview", "cogo:publish_gallery"],
        createdBy: input.createdBy,
        createdAt,
        durationMs: this.clock().getTime() - startedAt,
      }),
    );
    return output;
  }

  getGovernanceReport(workItemId: string): CogoXyzGovernanceReport | null {
    const workItem = this.workItems.get(workItemId);
    if (!workItem) return null;
    const bundle = Array.from(this.bundles.values())
      .reverse()
      .find((item) => item.workItemId === workItemId);
    const preview = Array.from(this.previews.values())
      .reverse()
      .find((item) => item.workItemId === workItemId);
    const evidence = Array.from(this.evidence.values()).filter(
      (item) => item.workItemId === workItemId,
    );
    const evidenceCounts = evidence.reduce<
      Partial<Record<CogoXyzEvidenceType, number>>
    >((acc, item) => {
      acc[item.type] = (acc[item.type] ?? 0) + 1;
      return acc;
    }, {});
    const toolRuns = Array.from(this.toolRuns.values()).filter((run) => {
      const input = run.input;
      const output = run.output ?? {};
      return (
        input.workItemId === workItemId ||
        output.workItemId === workItemId ||
        (bundle &&
          (input.bundleId === bundle.id || output.bundleId === bundle.id)) ||
        (preview &&
          (input.previewUrl === preview.previewUrl ||
            output.previewUrl === preview.previewUrl))
      );
    });
    const permissionScopes = Array.from(
      new Set(toolRuns.flatMap((run) => run.permissionScope)),
    );
    const externalWritePermissions = permissionScopes.filter(
      isExternalWritePermission,
    );
    const approvalRequired = evidence.some(
      (item) => item.metadata?.approvalRequired === true,
    );
    const productionDeployRecorded = evidence.some(
      (item) =>
        item.type === "deploy_record" &&
        item.status === "deployed" &&
        item.metadata?.target === "production",
    );
    const proofLevel: CogoXyzGovernanceReport["proofLevel"] =
      workItem.status === "failed"
        ? "failed"
        : workItem.status === "archived"
          ? "archived"
          : productionDeployRecorded
            ? "deployed"
            : evidence.some(
                  (item) =>
                    item.status === "attached_to_source" ||
                    item.type === "gallery_publish" ||
                    item.type === "github_comment",
                )
              ? "attached"
              : evidence.some((item) => item.status === "browser_checked")
                ? "inspected"
                : preview
                  ? "previewed"
                  : bundle
                    ? "converted"
                    : "imported";
    return {
      workItemId,
      workspaceId: workItem.workspaceId,
      sourceType: workItem.sourceType,
      proofLevel,
      evidenceCounts,
      permissionScopes,
      externalWritePermissions,
      approvalRequired,
      productionDeployRecorded,
      auditLog: toolRuns.map((run) => ({
        toolRunId: run.id,
        toolName: run.toolName,
        status: run.status,
        permissionScope: run.permissionScope,
        createdAt: run.createdAt,
        durationMs: run.durationMs,
      })),
    };
  }

  getEvidencePacket(workItemId: string): CogoXyzEvidencePacket | null {
    const workItem = this.workItems.get(workItemId);
    if (!workItem) return null;
    const bundle = Array.from(this.bundles.values())
      .reverse()
      .find((item) => item.workItemId === workItemId);
    const preview = Array.from(this.previews.values())
      .reverse()
      .find((item) => item.workItemId === workItemId);
    return {
      workItem,
      bundle,
      preview,
      evidence: Array.from(this.evidence.values()).filter(
        (item) => item.workItemId === workItemId,
      ),
      toolRuns: Array.from(this.toolRuns.values()).filter(
        (item) => item.workspaceId === workItem.workspaceId,
      ),
    };
  }

  getToolRun(toolRunId: string): CogoXyzToolRun | null {
    return this.toolRuns.get(toolRunId) ?? null;
  }

  listLibraryItems(
    input: CogoXyzListLibraryItemsInput = {},
  ): CogoXyzListLibraryItemsOutput {
    const query = input.query?.trim().toLowerCase();
    const sourceType =
      input.sourceType && input.sourceType !== "all" ? input.sourceType : null;
    const proofLevel =
      input.proofLevel && input.proofLevel !== "all" ? input.proofLevel : null;
    const includeArchived = input.includeArchived === true;
    const limit =
      typeof input.limit === "number" &&
      Number.isFinite(input.limit) &&
      input.limit > 0
        ? Math.floor(input.limit)
        : null;
    const items = Array.from(this.workItems.values())
      .map((workItem) => this.toLibraryItem(workItem))
      .filter((item): item is CogoXyzLibraryItem => Boolean(item))
      .filter((item) => includeArchived || !item.isArchived)
      .filter(
        (item) =>
          !input.workspaceId || item.workItem.workspaceId === input.workspaceId,
      )
      .filter((item) => !sourceType || item.workItem.sourceType === sourceType)
      .filter((item) => !proofLevel || item.proofLevel === proofLevel)
      .filter((item) => {
        if (!query) return true;
        return [
          item.workItem.title,
          item.workItem.id,
          item.workItem.sourceType,
          item.workItem.status,
          item.preview?.previewUrl,
          item.bundle?.bundleRef,
        ].some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(query),
        );
      })
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    return {
      items: limit ? items.slice(0, limit) : items,
      total: items.length,
    };
  }

  getLibraryItem(workItemId: string): CogoXyzGetLibraryItemOutput | null {
    const workItem = this.workItems.get(workItemId);
    if (!workItem) return null;
    const item = this.toLibraryItem(workItem);
    const packet = this.getEvidencePacket(workItemId);
    const governance = this.getGovernanceReport(workItemId);
    if (!item || !packet || !governance) return null;
    return {
      ...item,
      packet,
      governance,
    };
  }

  archiveLibraryItem(
    workItemId: string,
    archived: boolean,
  ): CogoXyzArchiveLibraryItemOutput | null {
    const workItem = this.workItems.get(workItemId);
    if (!workItem) return null;
    const now = this.clock().toISOString();
    const nextStatus: CogoXyzWorkItemStatus = archived
      ? "archived"
      : this.statusFromRecords(workItemId);
    this.updateWorkItemStatus(workItemId, nextStatus, now);
    const item = this.toLibraryItem(this.workItems.get(workItemId) ?? workItem);
    return item ? { workItemId, item } : null;
  }

  reset(): void {
    this.workItems.clear();
    this.bundles.clear();
    this.previews.clear();
    this.evidence.clear();
    this.toolRuns.clear();
    this.previewByUrl.clear();
    this.persist();
  }

  /**
   * Sign-out storage hygiene (Studio Wave 3 Lane C, 2026-08-17).
   *
   * `reset()` above clears in-memory state but still PERSISTS an empty (yet
   * present) snapshot via `this.persist()` — the storage key survives with
   * `{"workItems":[],...}` in it. That is fine for test/dev resets, but the
   * L1 logout walk
   * (`docs/superpowers/specs/2026-08-17-studio-slice4-logout-walk.md`) found
   * the real defect one level up: sign-out leaves the registry's storage key
   * — carrying the user's raw chat prompts, work items, bundles, and preview
   * URLs (~3.8KB in the walk's own run) — byte-identical on a shared
   * machine. This method is `reset()`'s stricter sibling: it also clears the
   * maps, but instead of re-persisting an empty snapshot it calls the
   * storage layer's own `clear()` (`LocalStorageCogoXyzRegistryStorage`
   * above) so the key is removed outright and unrecoverable. This is that
   * `clear()` method's first caller. Wired from the studio store's real
   * `signOut()` (`src/store/auth-slice.ts`).
   */
  clearOnSignOut(): void {
    this.workItems.clear();
    this.bundles.clear();
    this.previews.clear();
    this.evidence.clear();
    this.toolRuns.clear();
    this.previewByUrl.clear();
    this.storage?.clear?.();
    this.emitChange();
  }

  /**
   * Project-exit storage hygiene (Studio Wave 7 item 3, slice 6 §2.3b,
   * 2026-08-18) — `clearOnSignOut`'s project-scoped counterpart, measured
   * missing: the storage key held the CLOSED project's raw prompt at the
   * dashboard AND across a full page reload, and was replaced only when the
   * next project was created. Same mechanics deliberately (the key is
   * REMOVED, not re-persisted empty), different reason and caller: sign-out
   * protects one identity's content from the next identity on a shared
   * machine; this protects one project's content from the dashboard session
   * that follows it. The durable copy of a project's registry lives in
   * `project.settings.cogoXyzRegistry` (`cogo-xyz-project-storage.ts`, plus
   * server sync), so removing the device mirror here loses nothing a
   * project open cannot restore. Wired from `closeProject`
   * (`src/store/project-slice.ts`).
   */
  clearOnProjectExit(): void {
    this.clearOnSignOut();
  }

  snapshot(): CogoXyzRegistrySnapshot {
    return {
      version: COGO_XYZ_REGISTRY_SNAPSHOT_VERSION,
      workItems: Array.from(this.workItems.values()),
      bundles: Array.from(this.bundles.values()),
      previews: Array.from(this.previews.values()),
      evidence: Array.from(this.evidence.values()),
      toolRuns: Array.from(this.toolRuns.values()),
    };
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getRevision(): number {
    return this.revision;
  }

  restore(snapshot: CogoXyzRegistrySnapshot): void {
    this.applySnapshot(snapshot);
    this.persist();
  }

  private createWorkItem(params: {
    workspaceId: string;
    title: string;
    sourceType: CogoXyzSourceType;
    sourceRef: CogoXyzSourceRef;
    createdBy?: string;
    status: CogoXyzWorkItemStatus;
    createdAt: string;
  }): CogoXyzWorkItem {
    const workItem: CogoXyzWorkItem = {
      id: this.idFactory("workitem"),
      workspaceId: params.workspaceId,
      title: params.title,
      sourceType: params.sourceType,
      sourceRef: params.sourceRef,
      status: params.status,
      ...(params.createdBy === undefined
        ? {}
        : { createdBy: params.createdBy }),
      createdAt: params.createdAt,
      updatedAt: params.createdAt,
    };
    this.workItems.set(workItem.id, workItem);
    this.persist();
    return workItem;
  }

  private createBundle(
    workItemId: string,
    createdAt: string,
    options: Pick<CogoXyzCreateCogoAppOptions, "bundleRef"> = {},
  ): CogoXyzCogoBundle {
    const id = this.idFactory("bundle");
    const bundle: CogoXyzCogoBundle = {
      id,
      workItemId,
      schemaVersion: "cogo-xyz.phase1.v1",
      generatedAt: createdAt,
      sourceSnapshotRef: inputSnapshotRef(workItemId),
      bundleRef: options.bundleRef ?? bundleRef(id),
      validationStatus: "not_run",
    };
    this.bundles.set(bundle.id, bundle);
    this.persist();
    return bundle;
  }

  private createPreview(
    workItemId: string,
    bundleId: string,
    createdAt: string,
    previewUrlOverride?: string,
  ): CogoXyzPreview {
    const previewId = this.idFactory("preview");
    const preview: CogoXyzPreview = {
      id: previewId,
      workItemId,
      bundleId,
      previewUrl:
        previewUrlOverride ??
        previewUrl(this.previewOrigin, workItemId, previewId),
      viewport: "responsive",
      status: "preview_generated",
      createdAt,
    };
    this.previews.set(preview.id, preview);
    this.previewByUrl.set(preview.previewUrl, preview.id);
    this.persist();
    return preview;
  }

  private recordEvidence(
    params: Omit<CogoXyzEvidenceArtifact, "id">,
  ): CogoXyzEvidenceArtifact {
    const evidence: CogoXyzEvidenceArtifact = {
      id: this.idFactory("evidence"),
      ...params,
    };
    this.evidence.set(evidence.id, evidence);
    this.persist();
    return evidence;
  }

  private recordToolRun(toolRun: CogoXyzToolRun): CogoXyzToolRun {
    this.toolRuns.set(toolRun.id, toolRun);
    this.persist();
    return toolRun;
  }

  private updateWorkItemStatus(
    workItemId: string,
    status: CogoXyzWorkItemStatus,
    updatedAt: string,
  ): void {
    const workItem = this.workItems.get(workItemId);
    if (!workItem) return;
    this.workItems.set(workItemId, { ...workItem, status, updatedAt });
    this.persist();
  }

  private updatePreviewStatus(
    previewId: string,
    status: CogoXyzPreview["status"],
  ): void {
    const preview = this.previews.get(previewId);
    if (!preview) return;
    this.previews.set(previewId, { ...preview, status });
    this.persist();
  }

  private toLibraryItem(workItem: CogoXyzWorkItem): CogoXyzLibraryItem | null {
    const bundle = Array.from(this.bundles.values())
      .reverse()
      .find((item) => item.workItemId === workItem.id);
    const preview = Array.from(this.previews.values())
      .reverse()
      .find((item) => item.workItemId === workItem.id);
    const evidence = Array.from(this.evidence.values()).filter(
      (item) => item.workItemId === workItem.id,
    );
    const governance = this.getGovernanceReport(workItem.id);
    if (!governance) return null;
    const toolRunCount = Array.from(this.toolRuns.values()).filter((run) => {
      const input = run.input;
      const output = run.output ?? {};
      return (
        input.workItemId === workItem.id ||
        output.workItemId === workItem.id ||
        (bundle &&
          (input.bundleId === bundle.id || output.bundleId === bundle.id)) ||
        (preview &&
          (input.previewUrl === preview.previewUrl ||
            output.previewUrl === preview.previewUrl))
      );
    }).length;
    const latestEvidenceAt = evidence
      .map((item) => item.createdAt)
      .sort()
      .at(-1);
    return {
      workItem,
      bundle,
      preview,
      proofLevel: governance.proofLevel,
      evidenceCount: evidence.length,
      toolRunCount,
      updatedAt:
        latestEvidenceAt && latestEvidenceAt > workItem.updatedAt
          ? latestEvidenceAt
          : workItem.updatedAt,
      isArchived: workItem.status === "archived",
    };
  }

  private statusFromRecords(workItemId: string): CogoXyzWorkItemStatus {
    const bundle = Array.from(this.bundles.values())
      .reverse()
      .find((item) => item.workItemId === workItemId);
    const preview = Array.from(this.previews.values())
      .reverse()
      .find((item) => item.workItemId === workItemId);
    const evidence = Array.from(this.evidence.values()).filter(
      (item) => item.workItemId === workItemId,
    );
    if (
      evidence.some(
        (item) =>
          item.type === "deploy_record" &&
          item.status === "deployed" &&
          item.metadata?.target === "production",
      )
    ) {
      return "deployed";
    }
    if (
      evidence.some(
        (item) =>
          item.status === "attached_to_source" ||
          item.type === "gallery_publish" ||
          item.type === "github_comment",
      )
    ) {
      return "attached";
    }
    if (evidence.some((item) => item.status === "browser_checked"))
      return "inspected";
    if (preview) return "preview_generated";
    if (bundle) return "converted";
    return "created";
  }

  private resolveWorkItemIdForPreview(url: string): string | null {
    const previewId = this.previewByUrl.get(url);
    const preview = previewId ? this.previews.get(previewId) : undefined;
    return preview?.workItemId ?? null;
  }

  private hydrate(): void {
    const snapshot = this.storage?.load();
    if (!snapshot) return;
    this.applySnapshot(snapshot);
  }

  private applySnapshot(snapshot: CogoXyzRegistrySnapshot): void {
    this.workItems.clear();
    this.bundles.clear();
    this.previews.clear();
    this.evidence.clear();
    this.toolRuns.clear();
    this.previewByUrl.clear();
    for (const item of snapshot.workItems) this.workItems.set(item.id, item);
    for (const item of snapshot.bundles) this.bundles.set(item.id, item);
    for (const item of snapshot.previews) {
      this.previews.set(item.id, item);
      this.previewByUrl.set(item.previewUrl, item.id);
    }
    for (const item of snapshot.evidence) this.evidence.set(item.id, item);
    for (const item of snapshot.toolRuns) this.toolRuns.set(item.id, item);
  }

  private persist(): void {
    this.storage?.save(this.snapshot());
    this.emitChange();
  }

  private emitChange(): void {
    this.revision += 1;
    for (const listener of this.listeners) listener();
  }
}

export const cogoXyzRegistry = new CogoXyzRegistry();
